// Buscas da tela do Faturamento (#6500). Cada uma é pequena e independente.
import { useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { carregarUsinasVigor } from '../../src/lib/usinasVigor';
import { buscarEmissoes } from '../Dashboard/DashboardFaturamento/hooks/useDashboardFaturamentoData';
import type { EmissaoFaturamento } from '../../src/lib/financeiro/metricasFaturamento';
import {
  faturasParaRevisar, filaDoFaturamento, type FaturaParaRevisar, type FaturaPulada, type FilaDoFaturamento,
} from '../../src/lib/visaoGeral/faturamento';

const PAGINA = 1000;
const STATUS_DA_FILA = '"Fatura disponível","Aguardando validação","Fatura validada","Com boleto calculado"';

async function paginar<T>(montar: () => any): Promise<T[]> {
  const todas: T[] = [];
  for (let p = 0; ; p++) {
    const { data, error } = await montar().range(p * PAGINA, p * PAGINA + PAGINA - 1);
    if (error) throw error;
    todas.push(...((data || []) as T[]));
    if (!data || data.length < PAGINA) break;
  }
  return todas;
}

/** Faturas paradas em validação, cálculo e emissão (mesma régua de Faturas e Cobranças). */
export function useFilaDoFaturamento(): { fila: FilaDoFaturamento | null; erro: string | null } {
  const [estado, setEstado] = useState<{ fila: FilaDoFaturamento | null; erro: string | null }>({ fila: null, erro: null });
  useEffect(() => {
    let vivo = true;
    paginar<any>(() => supabase.from('faturas')
      .select('id, status_fatura, classificacao, cobranca_agrupada, pular_calculo_em')
      .eq('ambiente', 'producao')
      .is('excluido_em', null)
      .or(`status_fatura.is.null,status_fatura.in.(${STATUS_DA_FILA})`)
      .order('id'))
      .then(linhas => { if (vivo) setEstado({ fila: filaDoFaturamento(linhas), erro: null }); })
      .catch(e => { console.error('Fila do faturamento:', e); if (vivo) setEstado({ fila: null, erro: 'Não foi possível carregar a fila do faturamento.' }); });
    return () => { vivo = false; };
  }, []);
  return estado;
}

const CAMPOS_PULO = [
  'id', 'uc_codigo', 'cliente_nome', 'mes_referencia', 'pular_calculo_em', 'pular_calculo_por_nome',
  'motivo_pular_calculo', 'motivo_pular_calculo_codigo', 'energia_injetada_gd1_kwh', 'energia_injetada_gd2_kwh',
  ...[1, 2, 3, 4, 5].flatMap(n => [`unidade_geradora_${n}`, `injecao_kwh_ug_${n}`]),
].join(', ');

/**
 * Puladas dos meses de referência indicados ("OUT/2026") com injeção e um motivo que
 * não vale para a origem dela. `null` enquanto carrega; lista vazia sem nada a revisar.
 * `semUsinas` = não deu para ler a lista de usinas da Vigor (sem ela não há como separar).
 */
export function useFaturasParaRevisar(meses: string[]): { lista: FaturaParaRevisar[] | null; semUsinas: boolean } {
  const [estado, setEstado] = useState<{ lista: FaturaParaRevisar[] | null; semUsinas: boolean }>({ lista: null, semUsinas: false });
  const chave = meses.join(',');
  useEffect(() => {
    let vivo = true;
    (async () => {
      const [usinas, puladas] = await Promise.all([
        carregarUsinasVigor(),
        paginar<FaturaPulada>(() => supabase.from('faturas')
          .select(CAMPOS_PULO)
          .eq('ambiente', 'producao')
          .is('excluido_em', null)
          .not('pular_calculo_em', 'is', null)
          .is('emitido_em', null)
          .in('mes_referencia', chave.split(','))
          .order('id')),
      ]);
      const lista = faturasParaRevisar(puladas, usinas);
      if (vivo) setEstado({ lista: lista ?? [], semUsinas: lista === null });
    })().catch(e => { console.error('Faturas para revisar:', e); if (vivo) setEstado({ lista: [], semUsinas: false }); });
    return () => { vivo = false; };
  }, [chave]);
  return estado;
}

/** Emissões (1ª de cada fatura, achatadas) desde `desde`, e os pulos da pessoa no mesmo período. */
export function useProducaoDoMes(desde: Date, nome: string | null): { emissoes: EmissaoFaturamento[] | null; pulosPorDia: Map<string, number> | null } {
  const [estado, setEstado] = useState<{ emissoes: EmissaoFaturamento[] | null; pulosPorDia: Map<string, number> | null }>({ emissoes: null, pulosPorDia: null });
  const desdeIso = desde.toISOString();
  useEffect(() => {
    let vivo = true;
    setEstado({ emissoes: null, pulosPorDia: null });
    (async () => {
      const [emissoes, pulos] = await Promise.all([
        buscarEmissoes(new Date(desdeIso)),
        nome
          ? paginar<{ pular_calculo_em: string }>(() => supabase.from('faturas')
              .select('id, pular_calculo_em')
              .eq('pular_calculo_por_nome', nome)
              .gte('pular_calculo_em', desdeIso)
              .order('id'))
          : Promise.resolve([]),
      ]);
      const porDia = new Map<string, number>();
      for (const p of pulos) {
        const d = new Date(p.pular_calculo_em);
        const dia = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        porDia.set(dia, (porDia.get(dia) ?? 0) + 1);
      }
      if (vivo) setEstado({ emissoes, pulosPorDia: porDia });
    })().catch(e => { console.error('Produção do mês:', e); if (vivo) setEstado({ emissoes: [], pulosPorDia: new Map() }); });
    return () => { vivo = false; };
  }, [desdeIso, nome]);
  return estado;
}
